import { useState, useEffect, useRef } from 'react';
import { ScrollText, Trophy, Download, Eye, X, QrCode, CheckCircle, Loader2 } from 'lucide-react';
import { getCertificates, downloadCertificate } from '../../services/certificates';

export default function Certificates() {
  const [certs, setCerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previewCert, setPreviewCert] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [downloading, setDownloading] = useState({});
  const objectUrlRef = useRef(null);

  useEffect(() => {
    getCertificates().then(r => setCerts(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  // Cleanup object URL on unmount
  useEffect(() => {
    return () => { if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current); };
  }, []);

  const openPreview = async (cert) => {
    setPreviewCert(cert);
    setPreviewLoading(true);
    setPreviewUrl(null);
    try {
      const res = await downloadCertificate(cert.id);
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = url;
      setPreviewUrl(url);
    } catch {
      setPreviewUrl(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    setPreviewCert(null);
    setPreviewUrl(null);
    if (objectUrlRef.current) { URL.revokeObjectURL(objectUrlRef.current); objectUrlRef.current = null; }
  };

  const handleDownload = async (cert) => {
    setDownloading(d => ({ ...d, [cert.id]: true }));
    try {
      const res = await downloadCertificate(cert.id);
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `sertifikat-${cert.certificate_number}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      /* ignore */
    } finally {
      setDownloading(d => ({ ...d, [cert.id]: false }));
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent"></div>
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-600 to-teal-600 rounded-2xl p-8 text-white text-center">
        <h1 className="text-3xl font-bold mb-2 flex items-center justify-center gap-2">
          <ScrollText className="w-8 h-8" /> Sertifikatlarim
        </h1>
        <p className="text-green-100">{certs.length} ta sertifikat</p>
      </div>

      {/* Certificate cards */}
      {certs.length > 0 ? (
        <div className="grid md:grid-cols-2 gap-4">
          {certs.map(c => (
            <div key={c.id} className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-100 dark:border-gray-700 shadow-sm hover:shadow-md transition">
              {/* Top row */}
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-xl flex items-center justify-center shrink-0">
                  <Trophy className="w-6 h-6 text-white" />
                </div>
                <div className="min-w-0">
                  <h3 className="font-bold text-gray-900 dark:text-white truncate">{c.course?.title}</h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400">#{c.certificate_number}</p>
                </div>
              </div>

              {/* Meta row */}
              <div className="flex items-center justify-between text-sm mb-4">
                <span className="text-gray-500 dark:text-gray-400">
                  {new Date(c.issued_at).toLocaleDateString('uz')}
                </span>
                {c.grade && (
                  <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded-full text-xs font-medium">
                    Baho: {c.grade}
                  </span>
                )}
              </div>

              {/* Action buttons */}
              <div className="flex gap-2">
                <button
                  onClick={() => openPreview(c)}
                  className="flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-xl bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 text-sm font-medium hover:bg-blue-100 dark:hover:bg-blue-900/50 transition"
                >
                  <Eye className="w-4 h-4" /> Ko'rish
                </button>
                <button
                  onClick={() => handleDownload(c)}
                  disabled={downloading[c.id]}
                  className="flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-xl bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400 text-sm font-medium hover:bg-green-100 dark:hover:bg-green-900/50 transition disabled:opacity-50"
                >
                  {downloading[c.id]
                    ? <><Loader2 className="w-4 h-4 animate-spin" /> Yuklanmoqda...</>
                    : <><Download className="w-4 h-4" /> Yuklab olish</>
                  }
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-12 text-center border border-gray-100 dark:border-gray-700">
          <div className="mb-4 flex justify-center"><ScrollText className="w-12 h-12 text-gray-300" /></div>
          <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">Hali sertifikat yo'q</h3>
          <p className="text-gray-500 dark:text-gray-400">Kursni tugatib, sertifikat oling</p>
        </div>
      )}

      {/* PDF Preview Modal */}
      {previewCert && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={closePreview}>
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-5xl max-h-[95vh] flex flex-col" onClick={e => e.stopPropagation()}>
            {/* Modal header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <Trophy className="w-5 h-5 text-yellow-500" />
                <div>
                  <p className="font-semibold text-gray-900 dark:text-white text-sm">{previewCert.course?.title}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">#{previewCert.certificate_number}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleDownload(previewCert)}
                  disabled={downloading[previewCert.id]}
                  className="flex items-center gap-1.5 py-1.5 px-4 rounded-xl bg-green-600 hover:bg-green-700 text-white text-sm font-medium transition disabled:opacity-50"
                >
                  {downloading[previewCert.id]
                    ? <Loader2 className="w-4 h-4 animate-spin" />
                    : <Download className="w-4 h-4" />
                  }
                  Yuklab olish
                </button>
                <button onClick={closePreview} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 transition">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* PDF viewer */}
            <div className="flex-1 overflow-hidden rounded-b-2xl bg-gray-100 dark:bg-gray-950 min-h-0">
              {previewLoading ? (
                <div className="flex flex-col items-center justify-center h-96 gap-3">
                  <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
                  <p className="text-sm text-gray-500">Sertifikat yuklanmoqda...</p>
                </div>
              ) : previewUrl ? (
                <iframe
                  src={previewUrl + '#toolbar=0&navpanes=0&scrollbar=0'}
                  className="w-full h-full min-h-[70vh] rounded-b-2xl"
                  title="Sertifikat"
                />
              ) : (
                <div className="flex flex-col items-center justify-center h-96 gap-3">
                  <p className="text-sm text-gray-500">Yuklanishda xatolik yuz berdi</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}